import { createPage, Block, Title, Container, Flex, Slot, If } from 'react-commons';
import Meta from '@/components/Meta';

import FluidLayout from '@/layouts/FluidLayout';
import DailyHorizontalNav from '@/components/DailyHorizontalNav';
import style from './index.module.scss';

import aboutAsideMenu from '@/lib/data/aboutAsideMenu.json';
import { ArticlePageProps, fetchArticlePageProps } from '@/lib/util/fetchArticle';
import AppHeader from '@/components/AppHeader';
import { useThemeStore } from '@/lib/stores/theme';

export default createPage<ArticlePageProps>('AboutPage', { style }, function AboutPage ({ className }, props) {
  const metaTitle = 
    'About Shockwave' + 
    ' | Shockwave Games';

  const [ themeState ] = useThemeStore();
  const isClassic = themeState.layout === 'classic';

  return (
    <FluidLayout className={className}>

      {/* Meta information */}
      <Meta 
        title={metaTitle}
        titleTag={metaTitle}
      />

      {
        If(isClassic, () => (
          <Slot name='appHeader'>
            <AppHeader />
          </Slot>
        )).EndIf()
      }

      <Container>

        <DailyHorizontalNav links={aboutAsideMenu} isClassic />

        <div className='AboutPage__Content'>

          <Title h1 className='--isMediumText --isFontWeightLighter PageHeader'>Shockwave: a history 25 years in the making</Title>

          <Block>

            <Flex className='LogoSideBySide' justifySpaceBetween>
              <img src='/images/about/old_sw_logo.png' alt='' />
              <img src='/images/about/new_sw_logo.png' alt='' />
            </Flex>
          </Block>

          <Block>
            <Flex className='YearSideBySide' justifySpaceBetween>

              <Flex fit />
              <Flex rigid tag='p'>1998</Flex>
              <Flex fit className='Line' />
              <Flex rigid tag='p'>2025</Flex>
              <Flex fit />

            </Flex>
          </Block>

          <Block className='HeroBlock'>

            <p>Shockwave.com, launched in 1998 by Macromedia, has a rich history as a pioneer in online gaming and interactive media. Initially showcasing Shockwave and Flash player capabilities, it quickly became a leading platform for games, collaborating with iconic brands like Disney and Pixar. Over the decades, Shockwave evolved through strategic mergers, partnerships, and acquisitions, including its exclusive partnership with Rob Gordon's The Article 19 Group, which has delivered top daily games like the beloved Daily Jigsaw. Now, under the guidance of new leadership and driven by a renewed vision, Shockwave blends nostalgia with innovation, offering a curated mix of classic and modern games. With a strong focus on daily games and a commitment to casual gaming excellence.</p>

          </Block>


          <Block className='Block--withBackground Block--1998'>

            <div className='backgroundGraphic'>
              <img src='/images/about/dot.svg' alt='' />

              <svg preserveAspectRatio='none' viewBox="0 0 468 273" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path opacity="0.75" d="M42.352 1C15.0187 35.5 -26.448 123.1 26.352 197.5C52.5187 230.667 163.852 282.2 399.852 223C423.852 215.5 470.852 214.9 466.852 272.5" stroke="#195AAB" strokeLinejoin="bevel" strokeDasharray="12 12"/>
              </svg>
            </div>

            <div className='TitleWithYear TitleWithYear--Left'>
              <p>1998</p>
              <Title h2>Founding Years</Title>
            </div>

            <Flex className='SideBySide' alignCenter>
              <div>
                <p>Shockwave.com, launched in 1998 by Macromedia, showcased Shockwave and Flash player capabilities with simple games and interactive content. By 2000, it became a leading destination for online games and animations, drawing a growing user base.</p>

                <p>In its early years, Shockwave partnered with brands like Disney and Pixar to create branded games and interactive experiences, becoming one of the first online gaming portals.</p>
              </div>

              <div className='photoWithCaption'>
                <img src='/images/about/macromedia_logo.png' alt='' />

                <p>Macromedia</p>
              </div>
            </Flex>
            
          </Block>


          <Block className='Block--withBackground Block--2001'>

            <div className='backgroundGraphic'>
              <img src='/images/about/dot.svg' alt='' />

              <svg preserveAspectRatio='none' viewBox="0 0 402 290" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path opacity="0.75" d="M396 0.5C414.833 81.6667 385.2 236.6 116 207C72.3335 196.333 -11.5998 197.8 2.00016 289" stroke="#195AAB" strokeLinejoin="bevel" strokeDasharray="12 12"/>
              </svg>
            </div>

            <div className='TitleWithYear TitleWithYear--Right'>
              <p>2001</p>
              <Title h2>Expansion and Acquisitions</Title>
            </div>

            <Flex className='SideBySide SideBySide--Reverse' alignCenter>
              <div className='photoWithCaption'>
                <img src='/images/about/michael.png' alt='' />

                <p>Michael Moritz</p>
              </div>

              <p>In 2001, Shockwave merged with AtomFilms and later Addicting Games, expanding its content to include games, short films, and animations. Viacom acquired the company in 2004, bringing it under MTV Networks. Michael Moritz, a notable venture capitalist and former journalist, played a key role as an investor and director, leveraging his expertise and connections to drive growth.</p>
            </Flex>

          </Block>


          <Block className='Block--withBackground Block--2010'>

            <div className='backgroundGraphic'>
              <img src='/images/about/dot.svg' alt='' />

              <svg preserveAspectRatio="none" viewBox="0 0 463 291" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path opacity="0.75" d="M23 0.5C-4.83331 73.5 -28.3 219.4 100.5 219C222.167 213.167 464.7 219.2 461.5 290" stroke="#195AAB" strokeLinejoin="bevel" strokeDasharray="12 12"/>
              </svg>
            </div>

            <div className='TitleWithYear TitleWithYear--Left'>
              <p>2010</p>
              <Title h2>Transition to Addicting Games</Title>
            </div>

            <Flex className='SideBySide' alignCenter>
              <p>In 2010, Shockwave shifted focus to casual browser-based games after being sold by Viacom. It operated as an employee-owned company until its 2021 acquisition by Enthusiast Gaming. In 2017, founders Bill Karamouzis and Rhys Jones returned, reuniting the original team and driving a platform revival. This helped rejuvenate the platform, leading to a resurgence in popularity.</p>

              <div className='photoWithCaption'>
                <img src='/images/about/original_team.png' alt='' />

                <p>Original Addicting Games Team</p>
              </div>
            </Flex>

          </Block>


          <Block className='Block--withBackground Block--2020'>

            <div className='backgroundGraphic'>
              <img src='/images/about/dot.svg' alt='' />

              <svg preserveAspectRatio="none" viewBox="0 0 533 288" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path opacity="0.75" d="M518 0.5C542.167 65.1667 552.4 197.9 400 211.5C289.5 215 54.9 235 0.5 287" stroke="#195AAB" strokeLinejoin="bevel" strokeDasharray="12 12"/>
              </svg>
            </div>

            <div className='TitleWithYear TitleWithYear--Right'>
              <p>2020</p>
              <Title h2>Shockwave's Modern Era</Title>
            </div>

            <Flex className='SideBySide SideBySide--Reverse' alignCenter>

              <div className='photoWithCaption'>
                <img src='/images/about/rob.png' alt='' />

                <p>Rob Gordon</p>
              </div>

              <div>
                <p>Shockwave, based in Los Angeles, is now an independent gaming company delivering high-quality experiences with a mix of classic and new games for nostalgic and modern players.</p>
                <p>Since the early 2000s, designer Rob Gordon's Article 19 Group has been a key partner, first contributing games to Shockwave's predecessor, ShockRave. In 2002, Gordon took over production of the Daily Jigsaw, the site’s top game, leading to an exclusive catalog of over 225 daily titles. This partnership continues to bring exciting content to players worldwide.</p>
              </div>

            </Flex>
            
          </Block>

          <Block>
            <div className='TitleWithYear TitleWithYear--Left'>
              <p>Now</p>
              <Title h2>Future Forward</Title>
            </div>

            <Flex className='SideBySide' alignCenter>

              <div>
                <p>Shockwave has evolved from an interactive media showcase to a leading gaming portal, staying relevant and engaging for gamers worldwide. With daily games for all players on any device, and collaborations with global developers, Shockwave continues to be the go-to destination for creative interactive content.</p>
              </div>

              <div className='photoWithCaption'>
                <img src='/images/about/supercali.png' alt='' className='--padSm2' />

                <p>Supercalifragilisticexpialidocious</p>
              </div>

            </Flex>

          </Block>

          <Block>
            <Flex className='AboutPage__Content__Founders' justifySpaceBetween alignCenter>
              <div className='photoWithCaption'>
                <img src='/images/about/bill.png' alt='' />

                <p>Bill Karamouzis</p>
                <p>Chief Executive Officer</p>
              </div>

              <div className='photoWithCaption'>
                <img src='/images/about/rhys.png' alt='' />

                <p>Rhys Jones</p>
                <p>Chief Product Officer</p>
              </div>

              <div className='photoWithCaption'>
                <img src='/images/about/erin.png' alt='' />

                <p>Erin Torbiak</p>
                <p>Chief Technical Officer</p>
              </div>
            </Flex>
          </Block>

        </div>

      </Container>

    </FluidLayout>
  );

});

export async function getStaticProps () {
  try {
    return {
      props: await fetchArticlePageProps('about'),
      revalidate: process.env.APP_CONFIG.REVALIDATE_INTERVAL_SHORT,
    };
  } catch (error) {
    throw error;
  }
}